import { isAxiosError } from "axios";
import { ReportJobStatus } from "../../../shared/interfaces/JobStatus";
import type { Job } from "../../../store/jobStore";
import type { JobStatusSnapshot } from "../api/jobStatusApi";

/** Server statuses the UI folds into PENDING — the drawer/badge/max-jobs
 *  logic only ever sees PENDING for a running pipeline. */
const RESUMABLE_STATUSES = new Set<string>([
  ReportJobStatus.PENDING,
  ReportJobStatus.STARTED,
  ReportJobStatus.GENERATING,
  ReportJobStatus.EXTRACTING,
]);

/** Legacy failure copy, preserved verbatim from the old checkJobStatus. */
export function jobFailureMessage(error: unknown): string {
  let message =
    "An error occurred while processing the video. Error found: " +
    ((error as Error)?.message || "");
  if (isAxiosError(error) && error.response) {
    message += " " + (error.response.data?.error || "");
  }
  return message;
}

/** The failure copy for a server-reported FAILED status (the old code threw
 *  `new Error(message || "Video processing failed.")` into the same path). */
export function jobFailedStatusMessage(snapshotMessage?: string): string {
  return jobFailureMessage(
    new Error(snapshotMessage || "Video processing failed."),
  );
}

/**
 * Pure transition reducer: folds a /jobStatus snapshot into a registry Job,
 * reproducing the old updateJobStatus/updateJobProgress/updateJobCompletionData
 * composition. Returns the SAME reference when nothing changed, so the
 * query-sync effect can re-apply snapshots idempotently without re-renders.
 *
 * Dropped (deliberately): the old `reportName: reportId` clobber inside
 * updateJobStatus — nothing ever set reportName, so it was a no-op.
 */
export function applyJobStatusSnapshot(job: Job, snapshot: JobStatusSnapshot): Job {
  const { jobStatus, message } = snapshot;

  let next: Job;
  if (jobStatus === ReportJobStatus.AWAITING_INPUT) {
    next = {
      ...job,
      status: ReportJobStatus.AWAITING_INPUT,
      error: undefined,
      progress: snapshot.progress ?? job.progress,
      progressMessage: message || job.progressMessage,
    };
  } else if (jobStatus === ReportJobStatus.AWAITING_TRANSCRIPT_DECISION) {
    next = {
      ...job,
      status: ReportJobStatus.AWAITING_TRANSCRIPT_DECISION,
      error: undefined,
      progress: snapshot.progress ?? job.progress,
      progressMessage: message || job.progressMessage,
    };
  } else if (jobStatus === ReportJobStatus.PENDING_REVIEW) {
    next = {
      ...job,
      status: ReportJobStatus.PENDING_REVIEW,
      reportId: snapshot.reportId ?? job.reportId,
      progress: snapshot.progress ?? 100,
      progressMessage: message || job.progressMessage,
      resumedAt: undefined,
    };
  } else if (jobStatus === ReportJobStatus.FAILED) {
    next = {
      ...job,
      status: ReportJobStatus.FAILED,
      // The pipeline's own statusMessage is written for the officer ("The
      // narrative could not be saved, so the full report was not generated.")
      // — show it as sent. The legacy wrapper only covers a message-less
      // FAILED, where there is nothing else to say.
      error: message || jobFailedStatusMessage(message),
      resumedAt: undefined,
    };
  } else if (RESUMABLE_STATUSES.has(jobStatus)) {
    next = {
      ...job,
      status: ReportJobStatus.PENDING,
      error: undefined,
      // The backend picked up the resume — close the grace window.
      resumedAt: undefined,
      // Resuming after human input starts a fresh visible activity cycle.
      displayStartedAt:
        job.status === ReportJobStatus.AWAITING_INPUT ||
        job.status === ReportJobStatus.AWAITING_TRANSCRIPT_DECISION
          ? new Date()
          : job.displayStartedAt,
      // Forward-only. A full report is two chained runs on one job, and the
      // second run's model reports on its own scale — it can hand back a
      // lower number right after the seam. A bar that walks backwards reads
      // as "it restarted". Cycle resets (retry, full-report approval) zero
      // `progress` on the entry itself, so they aren't held back by this.
      progress: Math.max(snapshot.progress ?? job.progress, job.progress),
      progressMessage: message || job.progressMessage,
    };
  } else {
    // Unknown status: merge progress only, keep polling semantics upstream.
    // Retain resumedAt — an unknown status proves neither that the job resumed
    // nor that it terminally completed, so the grace window must stay open.
    next = {
      ...job,
      progress: snapshot.progress ?? job.progress,
      progressMessage: message || job.progressMessage,
    };
  }

  // The reportId only ever arrives on a snapshot — POST /jobs answers with a
  // jobId alone, so the registry entry starts without one. Carry it on EVERY
  // status, not just PENDING_REVIEW: markJobResumed, markFullReportJob and the
  // plan-review navigation all resolve a job by reportId while it is still
  // AWAITING_INPUT, and they silently no-op against an entry that has none.
  if (snapshot.reportId && next.reportId !== snapshot.reportId) {
    next = { ...next, reportId: snapshot.reportId };
  }

  const changed = (Object.keys(next) as (keyof Job)[]).some(
    (key) => next[key] !== job[key],
  );
  return changed ? next : job;
}
